import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Image,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Stack,
} from "@chakra-ui/react";
import Card from "components/Card/Card";
import CardBody from "components/Card/CardBody";
import CardHeader from "components/Card/CardHeader";
import React, { useState, useMemo } from "react";
import { useTranslation } from 'react-i18next';
import { useRouteMatch, Link } from "react-router-dom";
import { getProductById, getImage } from "data/products";
import { useTranslatedProduct } from "data/productI18n";

function ProductDetail() {
  const { t } = useTranslation();
  const match = useRouteMatch();
  const { productId } = match.params;
  const textColor = "gray.700";

  const product = useMemo(() => getProductById(productId), [productId]);
  const translatedProduct = useTranslatedProduct(product);

  const defaultImage = useMemo(() => {
    if (!product) return null;
    const defaultImg = product.images.find((img) => img.isDefault);
    const key = defaultImg ? defaultImg.key : product.images[0]?.key;
    return key ? getImage(key) : null;
  }, [product]);

  const [currentImage, setCurrentImage] = useState(defaultImage);

  React.useEffect(() => {
    if (defaultImage) {
      setCurrentImage(defaultImage);
    }
  }, [defaultImage]);

  if (!product) {
    return (
      <Card mt={{ sm: "125px", md: "75px" }}>
        <CardBody>
          <Text color={textColor} fontSize="xl" fontWeight="bold">
            {t('common:productDetail.notFound')}
          </Text>
        </CardBody>
      </Card>
    );
  }

  const relatedWithImages = product.relatedProducts.map((rp) => {
    const rpProduct = getProductById(rp.id);
    const rpDefaultImg = rpProduct?.images?.find((i) => i.isDefault);
    const rpImageKey = rpDefaultImg
      ? rpDefaultImg.key
      : rpProduct?.images?.[0]?.key;
    return {
      ...rp,
      model: rpProduct?.model || rp.id,
      category: rpProduct?.category || "",
      imageKey: rpImageKey,
    };
  });

  return (
    <Card mt={{ sm: "125px", md: "75px" }}>
      <CardHeader mb="42px">
        <Text color={textColor} fontSize="lg" fontWeight="bold">
          {t('common:productDetail.title')}
        </Text>
      </CardHeader>
      <CardBody>
        <Flex direction="column" w="100%">
          {/* Top section: image gallery + info */}
          <Flex
            direction={{ sm: "column", lg: "row" }}
            mb={{ sm: "42px", lg: "84px" }}
          >
            {/* Image gallery */}
            <Flex
              direction="column"
              me={{ lg: "70px", xl: "120px" }}
              mb={{ sm: "24px", lg: "0px" }}
            >
              <Box
                w={{ sm: "275px", md: "670px", lg: "450px", xl: "600px" }}
                h={{ sm: "200px", md: "500px", lg: "330px", xl: "500px" }}
                mb="26px"
                mx={{ sm: "auto", lg: "0px" }}
              >
                <Image
                  src={currentImage}
                  w="100%"
                  h="100%"
                  borderRadius="15px"
                />
              </Box>
              <Stack
                direction="row"
                spacing={{ sm: "20px", md: "35px", lg: "20px" }}
                mx="auto"
                mb={{ sm: "24px", lg: "0px" }}
              >
                {product.images.map((img, idx) => {
                  const imgSrc = getImage(img.key);
                  if (!imgSrc) return null;
                  return (
                    <Box
                      key={img.key}
                      w={{ sm: "36px", md: "90px", lg: "60px" }}
                      h={{ sm: "36px", md: "90px", lg: "60px" }}
                    >
                      <Image
                        src={imgSrc}
                        w="100%"
                        h="100%"
                        borderRadius="15px"
                        cursor="pointer"
                        onClick={(e) => setCurrentImage(e.target.src)}
                      />
                    </Box>
                  );
                })}
              </Stack>
            </Flex>

            {/* Product info */}
            <Flex direction="column">
              <Text
                color={textColor}
                fontSize="3xl"
                fontWeight="bold"
                mb="12px"
              >
                {product.model}
              </Text>
              <Flex direction="row" mb="16px" flexWrap="wrap">
                {product.badges.map((badge, idx) => (
                  <React.Fragment key={idx}>
                    <Badge
                      colorScheme={badge.colorScheme}
                      h="28px"
                      px="12px"
                      mb="8px"
                      borderRadius="15px"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      fontSize="sm"
                    >
                      {badge.text}
                    </Badge>
                    &nbsp;
                  </React.Fragment>
                ))}
              </Flex>
              <Text color="gray.500" fontSize="lg" mb="24px">
                {product.description}
              </Text>
              {translatedProduct?.aiSummary && (
                <Flex
                  mb="32px"
                  p="20px 24px"
                  bg="linear-gradient(135deg, #f0f4ff 0%, #f8f9fc 100%)"
                  borderRadius="16px"
                  border="1px solid"
                  borderColor="blue.100"
                  align="flex-start"
                >
                  <Box
                    bg="#19418F"
                    color="white"
                    borderRadius="10px"
                    px="10px"
                    py="4px"
                    fontSize="xs"
                    fontWeight="bold"
                    mr="16px"
                    mt="2px"
                    flexShrink={0}
                  >
                    AI Summary
                  </Box>
                  <Text color="gray.700" fontSize="sm" lineHeight="tall">
                    {translatedProduct.aiSummary}
                  </Text>
                </Flex>
              )}
              <Flex direction="row">
                <Button
                  variant="no-hover"
                  bg="linear-gradient(81.62deg, #313860 2.25%, #151928 79.87%)"
                  w={{ sm: "240px", md: "100%", lg: "240px" }}
                  h="50px"
                  mx={{ sm: "auto", md: "0px" }}
                  color="#fff"
                  fontSize="sm"
                  fontWeight="bold"
                >
                  <a href={"/support"}>{t('common:buttons.manualDownload')}</a>
                </Button>
                &nbsp;
                <Button
                  variant="no-hover"
                  bg="linear-gradient(81.62deg, #313860 2.25%, #151928 79.87%)"
                  w={{ sm: "240px", md: "100%", lg: "240px" }}
                  h="50px"
                  mx={{ sm: "auto", md: "0px" }}
                  color="#fff"
                  fontSize="sm"
                  fontWeight="bold"
                >
                  <a
                    href="/software/Setup_IPManager_v2.1.0.8.exe.xz"
                    download
                  >
                    {t('common:buttons.ipManagerDownload')}
                  </a>
                </Button>
              </Flex>
            </Flex>
          </Flex>

          {/* Key Features - visual grid */}
          <Text color={textColor} fontSize="xl" fontWeight="bold" mb="20px">
            {t('common:productDetail.keyFeatures')}
          </Text>
          <Grid
            templateColumns={{ sm: "1fr", md: "1fr 1fr", xl: "repeat(3, 1fr)" }}
            gap="16px"
            mb="42px"
          >
            {translatedProduct?.keyFeatures.map((feature, idx) => (
              <Box
                key={idx}
                p="16px"
                border="1px solid"
                borderColor="gray.200"
                borderRadius="12px"
                bg="gray.50"
              >
                <Text color={textColor} fontSize="sm" fontWeight="500">
                  {feature}
                </Text>
              </Box>
            ))}
          </Grid>

          {/* AI Analysis section */}
          {product.aiTags && product.aiTags.length > 0 && (
            <>
              <Text color={textColor} fontSize="xl" fontWeight="bold" mb="20px">
                {t('common:productDetail.aiAnalysis')}
              </Text>
              <Box
                p="24px"
                border="1px solid"
                borderColor="blue.200"
                borderRadius="12px"
                bg="blue.50"
                mb="42px"
              >
                {product.aiTags.includes("iva") && (
                  <Box mb="16px">
                    <Text color="blue.700" fontSize="md" fontWeight="bold" mb="8px">
                      {t('common:productDetail.ivaTitle')}
                    </Text>
                    <Text color="gray.600" fontSize="sm">
                      {t('common:productDetail.ivaDescription')}
                    </Text>
                  </Box>
                )}
                {product.aiTags.includes("object-detection") && (
                  <Box mb="16px">
                    <Text color="blue.700" fontSize="md" fontWeight="bold" mb="8px">
                      {t('common:productDetail.objectDetectionTitle')}
                    </Text>
                    <Text color="gray.600" fontSize="sm">
                      {t('common:productDetail.objectDetectionDescription')}
                    </Text>
                  </Box>
                )}
                {product.aiTags.includes("event-thumbnail") && (
                  <Box>
                    <Text color="blue.700" fontSize="md" fontWeight="bold" mb="8px">
                      {t('common:productDetail.eventThumbnailTitle')}
                    </Text>
                    <Text color="gray.600" fontSize="sm">
                      {t('common:productDetail.eventThumbnailDescription')}
                    </Text>
                  </Box>
                )}
              </Box>
            </>
          )}

          {/* Other Products */}
          {relatedWithImages.length > 0 && (
            <>
              <Text color={textColor} fontSize="xl" fontWeight="bold" mb="20px">
                {t('common:productDetail.relatedProducts')}
              </Text>
              <Grid
                templateColumns={{ sm: "1fr", md: "repeat(2, 1fr)", xl: "repeat(3, 1fr)" }}
                gap="20px"
                mb="42px"
              >
                {relatedWithImages.map((rp, idx) => (
                  <Box
                    key={idx}
                    p="16px"
                    border="1px solid"
                    borderColor="gray.200"
                    borderRadius="12px"
                    _hover={{ borderColor: "#19418F", cursor: "pointer" }}
                    transition="all 0.2s"
                  >
                    <Link to={`/product/${rp.category}/${rp.id}`}>
                      <Flex align="center">
                        <Box w="60px" h="60px" me="16px" flexShrink={0}>
                          {rp.imageKey && (
                            <Image
                              src={getImage(rp.imageKey)}
                              w="100%"
                              h="100%"
                              borderRadius="8px"
                            />
                          )}
                        </Box>
                        <Box>
                          <Text color={textColor} fontSize="md" fontWeight="bold" mb="4px">
                            {rp.model}
                          </Text>
                          <Flex wrap="wrap">
                            {rp.badges.map((badge, bidx) => (
                              <Badge
                                key={bidx}
                                colorScheme="blue"
                                fontSize="xs"
                                mr="4px"
                                mb="2px"
                                borderRadius="8px"
                                px="6px"
                              >
                                {badge}
                              </Badge>
                            ))}
                          </Flex>
                        </Box>
                      </Flex>
                    </Link>
                  </Box>
                ))}
              </Grid>
            </>
          )}

          {/* Spec table - reference at bottom */}
          <Text color="textColor" fontSize="md" color="gray.400" fontWeight="normal" mb="12px">
            {t('common:productDetail.specReference')}
          </Text>
          <Box
            w="100%"
            overflowX={{ sm: "scroll", lg: "hidden" }}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="12px"
            mb="20px"
          >
            <Table variant="simple" w="100%" size="sm">
              <Thead bg="gray.50">
                <Tr>
                  <Th color="gray.500" fontSize="xs">
                    {t('common:productDetail.specFeature')}
                  </Th>
                  <Th color="gray.500" fontSize="xs">
                    {product.model}
                  </Th>
                </Tr>
              </Thead>
              <Tbody>
                {translatedProduct?.specs.map((spec, idx) => (
                  <Tr key={idx}>
                    <Td>
                      <Text color="gray.600" fontSize="sm">
                        {spec.feature}
                      </Text>
                    </Td>
                    <Td>
                      <Text color="gray.600" fontSize="sm">
                        {spec.value}
                      </Text>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        </Flex>
      </CardBody>
    </Card>
  );
}

export default ProductDetail;
